import { Box, Stack, Typography } from '@mui/material';
import { EMOJI_CATEGORY_TAB_ICONS, EmojiCategory } from './emojiData';

type Props = {
    categories: EmojiCategory[];
    selectedCategory: string;
    onSelectCategory: (label: string) => void;
    onSelectEmoji: (emoji: string) => void;
    readOnly?: boolean;
};

const EmojiSelector = ({ categories, selectedCategory, onSelectCategory, onSelectEmoji, readOnly }: Props) => {
    const activeEmojiCategory = categories.find((category) => category.label === selectedCategory) ?? categories[0];

    return (
        <Box
            sx={{
                borderLeft: '1px solid #ccc',
                borderRight: '1px solid #ccc',
                borderBottom: '1px solid #ccc',
                borderRadius: (theme) => theme.shape.borderRadius,
                mb: 1,
                backgroundColor: '#fff',
            }}
        >
            <Stack
                direction="row"
                spacing={0}
                sx={{
                    alignItems: 'center',
                    mb: 0.5,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                {categories.map((category) => (
                    <Box
                        key={`tab-${category.label}`}
                        component="button"
                        type="button"
                        onClick={() => onSelectCategory(category.label)}
                        sx={{
                            border: 'none',
                            background: selectedCategory === category.label ? 'action.selected' : 'transparent',
                            borderRadius: (theme) => theme.shape.borderRadius,
                            borderBottom: selectedCategory === category.label ? (theme) => `2px solid ${theme.palette.primary.main}` : 'none',
                            width: 30,
                            height: 30,
                            cursor: 'pointer',
                            fontSize: 17,
                            lineHeight: '24px',
                            textAlign: 'center',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                        title={category.label}
                    >
                        {EMOJI_CATEGORY_TAB_ICONS[category.label] ?? '🙂'}
                    </Box>
                ))}
            </Stack>

            {activeEmojiCategory && (
                <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={0} sx={{ minWidth: 'max-content', alignItems: 'center' }}>
                        {activeEmojiCategory.emojis.map((emoji) => (
                            <Box
                                key={`${activeEmojiCategory.label}-${emoji}`}
                                component="button"
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => onSelectEmoji(emoji)}
                                disabled={readOnly}
                                sx={{
                                    border: 'none',
                                    background: 'transparent',
                                    borderRadius: (theme) => theme.shape.borderRadius,
                                    width: 30,
                                    height: 30,
                                    cursor: readOnly ? 'default' : 'pointer',
                                    fontSize: 17,
                                    lineHeight: '24px',
                                    textAlign: 'center',
                                    p: 0,
                                    '&:hover': {
                                        backgroundColor: readOnly ? 'transparent' : 'action.hover',
                                    },
                                }}
                            >
                                {emoji}
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
            {!activeEmojiCategory && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    No emojis available
                </Typography>
            )}
        </Box>
    );
};

export default EmojiSelector;
